// Sidebar dropdown actions for channels, DMs, and group DMs.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { getActiveWorkspaceId, deleteChannelMessages } from './chatUtils.js'
// Every mutation below writes a workspace-scoped key (`channels`,
// `channelMembers`, `activeChannelId`, `messagesByChannel`) AFTER its SDK
// round-trip. Each captures its writer on the click tick, before that await —
// see functions/sameWorkspaceWriter.js for why the capture point is the rule.
import { sameWorkspaceWriter, wsIdOf } from './sameWorkspaceWriter.js'

// ── Context-menu / dropdown items for a sidebar row, by channel kind ──
// Single source of the per-type actions shared by the 3-dot menu and the
// right-click context menu. Returns [{ label, action, color? }]. The actions
// are dispatched in ChatSidebarDropdownItem.onClick.
export const chatContextItems = function (root, cid) {
  if (!root || !cid) return []
  const ch = (root.channels || []).find((c) => c.id === cid)
  if (!ch) return []
  const kind = ch.kind || 'channel'
  const me = root.currentUserEmail
  const mem = (root.channelMembers || []).find((m) => m.channel_id === cid && m.member_email === me)
  // Menu rows carry KEYS — the shared ContextDropdown item resolves
  // `s.label` through polyglot at its render site.
  const muteItem = {
    label: mem && mem.muted ? 'chat.menu.unmute' : 'chat.menu.mute',
    action: 'toggle-mute'
  }
  // Label reflects the CURRENT pinned state — root._pinnedChannels is the
  // same array Sidebar.js's PinnedGroup reads and the only thing `pinToTop`
  // below writes. Was a static 'Pin to top' regardless of state, so an
  // already-pinned channel's menu never offered 'Unpin' (the toggle itself
  // worked fine — only this label never re-read the state, INTERACT-1).
  const isPinned = (root._pinnedChannels || []).includes(cid)
  const pinItem = {
    label: isPinned ? 'chat.menu.unpin' : 'chat.menu.pinToTop',
    action: 'pin-to-top'
  }

  if (kind === 'channel') {
    return [
      { label: 'chat.menu.invite', action: 'invite-channel' },
      { label: 'chat.menu.rename', action: 'rename-channel' },
      { label: 'chat.menu.settings', action: 'channel-settings' },
      pinItem,
      muteItem
    ]
  }
  // Settings opens the same ChannelSettingsModal channels use — its
  // "Linked event / call" section (LinkedResources pills + calendar-event
  // picker) is kind-agnostic and the resourceLinks backend has no kind
  // restriction, but DMs/group DMs had NO menu entry to reach the modal, so
  // 1:1 conversations looked like they lacked linked-event/call support
  // entirely (2026-08-06 per Nika "personal (1-1) channels does not have
  // Linked event / call"). The modal itself already hides its channel-only
  // fields (name/topic/privacy/delete/add-members) for dm kinds.
  const settingsItem = { label: 'chat.menu.settings', action: 'channel-settings' }
  if (kind === 'group_dm') {
    return [
      { label: 'chat.menu.createChannel', action: 'convert-to-channel' },
      { label: 'chat.menu.rename', action: 'rename-channel' },
      settingsItem,
      pinItem,
      muteItem,
      { label: 'chat.menu.hide', action: 'hide-dm' },
      { label: 'chat.menu.leave', action: 'leave-channel', color: 'red' }
    ]
  }
  // 1:1 DM
  return [
    settingsItem,
    { label: 'chat.menu.startGroupChat', action: 'start-group-chat' },
    pinItem,
    muteItem,
    { label: 'chat.menu.hide', action: 'hide-dm' }
  ]
}

// ── Dispatch a context-menu action for a sidebar row ──
// The `actionFn` for the shared ContextDropdown when opened from a chat row.
// Reads the target channel from root.contextDropdown.channelId, closes the
// menu, then routes to the matching channel action. (Was inline in the old
// ChatSidebarDropdownItem.onClick.)
export const chatContextDispatch = async function (action, _item) {
  const root = this && this.state && this.state.root
  if (!root) return
  const channelId = root.contextDropdown && root.contextDropdown.channelId
  root.update({ contextDropdown: null })
  if (!action || !channelId) return
  // Every delegated action below (openRenameModal, openChannelSettings,
  // leaveChannel, …) rides the lazily-folded chat context (bundle-fold) —
  // this dispatch is on the eager slice precisely so it can fold first.
  const ok = await this.call('ensureChatContext')
  if (ok === false) return
  if (action === 'rename-channel') {
    this.call('openRenameModal', channelId)
  } else if (action === 'hide-dm') this.call('hideDM', channelId)
  else if (action === 'leave-channel') this.call('leaveChannel', channelId)
  else if (action === 'pin-to-top') this.call('pinToTop', channelId)
  else if (action === 'invite-channel' || action === 'channel-settings')
    this.call('openChannelSettings', channelId)
  else if (action === 'start-group-chat') this.call('startGroupChat', channelId)
  else if (action === 'convert-to-channel') this.call('convertToChannel', channelId)
  else if (action === 'toggle-mute') this.call('toggleChannelMute', channelId)
}

// ── Context-menu items for a teammate row with no DM yet (ChatDMPersonRow) ──
// Mirrors chatContextItems for the person-row branch of the 3-dot menu —
// SidebarItemMore falls back to this when a row carries `data-person-email`
// instead of `data-cid`. Dispatched by personContextDispatch below.
export const personContextItems = function (root, email) {
  if (!root || !email) return []
  const items = [
    { label: 'chat.menu.message', action: 'message', icon: 'chat' },
    { label: 'chat.menu.viewProfile', action: 'view-profile' }
  ]
  // openNewBookingModal ships from a sibling change in this same wave —
  // guard on registration so the menu is correct regardless of landing
  // order between the two changes.
  if (typeof this?.context?.functions?.openNewBookingModal === 'function') {
    items.push({ label: 'chat.menu.scheduleMeeting', action: 'schedule-meeting' })
  }
  return items
}

// ── Dispatch a context-menu action for a teammate row (see personContextItems) ──
// Reads the target email from root.contextDropdown.personEmail, closes the
// menu, then routes to the matching action. Mirrors chatContextDispatch.
export const personContextDispatch = async function (action, _item) {
  const root = this && this.state && this.state.root
  if (!root) return
  const email = root.contextDropdown && root.contextDropdown.personEmail
  root.update({ contextDropdown: null })
  if (!action || !email) return
  if (action === 'message') {
    // messageUser rides the lazily-folded chat context (bundle-fold) — fold
    // first. Only this branch: view-profile / schedule-meeting are shell /
    // calendar functions and must not gate on the chat chunk.
    const ok = await this.call('ensureChatContext')
    if (ok === false) return
    this.call('messageUser', email).catch((err) => {
      this.call('openNotification', {
        type: 'error',
        title: this.call('polyglot', 'chat.error.openChatTitle'),
        message: (err && err.message) || this.call('polyglot', 'chat.error.tryAgain')
      })
    })
  } else if (action === 'view-profile') this.call('openProfileModal', email)
  else if (action === 'schedule-meeting') this.call('openNewBookingModal', { invitees: email })
}

// ── Esc closes the chat channel/DM right-click + 3-dot context menu ──
// (INTERACT-1) ContextDropdown's own Escape handler (shared/components/
// ContextDropdown.js, Menu.onKeydown) only fires when the keydown's target is
// a DESCENDANT of the Menu node: DOMQL delegates keydown through one root
// listener that walks the ANCESTOR chain of event.target, same as native
// bubbling (smbls/packages/element/src/delegate.js — installDelegatedEvent).
// Opening the menu (right-click OR the 3-dot button) never moves focus into
// it, so pressing Escape right after opening — the exact repro — never
// reaches that handler. This isn't chat-specific (every ContextDropdown
// consumer has the same latent gap); this only closes chat's own openers.
// Mirrors the established fix for this exact class of bug: shared/components/
// Modal.js's global, focus-INDEPENDENT `document` keydown listener, bound
// once behind a window-flag guard so it runs exactly once regardless of
// mount order. `root` is cached on `el.context` — not module scope, which
// per Modal.js's own comment never runs once frank serializes this file for
// platform delivery — and re-read fresh from there on every keypress, so it
// always closes whichever contextDropdown is currently open.
export const ensureChatContextMenuEscClose = function () {
  if (typeof window === 'undefined') return
  const ctx = this && this.context
  const root = this && this.state && this.state.root
  if (!ctx || !root) return
  if (!ctx.__chatCtxDdEscRoot) ctx.__chatCtxDdEscRoot = root
  if (window.__chatCtxDdEscBound) return
  window.__chatCtxDdEscBound = true
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const r = ctx.__chatCtxDdEscRoot
    if (r && r.contextDropdown) r.update({ contextDropdown: null }, { preventFetch: true })
  })
}

// ── Rename channel or group DM ──
export const renameChannel = async function (channelId, newName) {
  const root = this?.state?.root
  if (!root || !channelId || !newName) return
  const _write = sameWorkspaceWriter(root)
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.chat update
    await getSDK().execute('workspaceProject.chat', 'update', {
      id: channelId,
      payload: { name: newName }
    })
  } catch (err) {
    console.error('[renameChannel]', err)
    return
  }
  _write({
    channels: (root.channels || []).map((c) => (c.id === channelId ? { ...c, name: newName } : c))
  })
}

// ── Hide DM — remove from sidebar without deleting ──
export const hideDM = async function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return
  const me = root.currentUserEmail
  // Optimistic: remove from local channels list
  const hiddenSet = new Set(root._hiddenChannels || [])
  hiddenSet.add(channelId)
  root.update({
    _hiddenChannels: Array.from(hiddenSet),
    activeChannelId: root.activeChannelId === channelId ? null : root.activeChannelId
  })
}

// ── Leave channel / group DM ──
export const leaveChannel = async function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return
  const me = root.currentUserEmail
  const _write = sameWorkspaceWriter(root)
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.chat.members remove
    await getSDK().execute('workspaceProject.chat.members', 'remove', {
      channelId,
      userId: me
    })
  } catch (err) {
    console.error('[leaveChannel]', err)
    return
  }
  _write({
    channelMembers: (root.channelMembers || []).filter(
      (m) => !(m.channel_id === channelId && m.member_email === me)
    ),
    channels: (root.channels || []).filter((c) => c.id !== channelId),
    activeChannelId: root.activeChannelId === channelId ? null : root.activeChannelId
  })
}

// ── Delete channel — irreversible, owner-only ──
// Hard-removes the channel via the SDK's `remove` op, then drops it from
// local state (channels list, membership rows, active selection). Messages
// + members cascade-delete on the server side via FK ON DELETE CASCADE in
// the chat_messages / chat_channel_members migrations. Unlike `leaveChannel`
// (which only removes the caller's membership row), this is a wholesale
// deletion — every member loses the channel.
export const deleteChannel = async function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return false
  const _write = sameWorkspaceWriter(root)
  // `messagesByChannel` is the one scoped key chat does not write as a patch,
  // so its guard travels as the captured id — see chatUtils.setChannelMessages.
  const _issuedFor = wsIdOf(root)
  try {
    // SDK route: workspaceProject.chat 'remove' → chat.removeChannel →
    // DELETE /chat/channels/:id on the workspace-project worker.
    await getSDK().execute('workspaceProject.chat', 'remove', { id: channelId })
  } catch (err) {
    console.error('[deleteChannel]', err?.message || err)
    return false
  }
  deleteChannelMessages(root, channelId, _issuedFor)
  _write({
    channels: (root.channels || []).filter((c) => c.id !== channelId),
    channelMembers: (root.channelMembers || []).filter((m) => m.channel_id !== channelId),
    activeChannelId: root.activeChannelId === channelId ? null : root.activeChannelId,
    showChannelSettings: root.showChannelSettings === channelId ? null : root.showChannelSettings
  })
  return true
}

// ── Pin to top — toggle pinned, persisted in localStorage ──
// Keyed PER WORKSPACE (workspace isolation — one workspace's pins must not
// bleed into another's sidebar). The legacy un-namespaced key is read once
// as a fallback so existing pins survive the migration.
const _LEGACY_PINNED_KEY = 'chat_pinned_channels'
const _pinnedKey = (root) => {
  const ws = getActiveWorkspaceId(root)
  return ws ? `${_LEGACY_PINNED_KEY}:${ws}` : _LEGACY_PINNED_KEY
}
const _loadPinned = (root) => {
  try {
    const scoped = localStorage.getItem(_pinnedKey(root))
    if (scoped != null) return JSON.parse(scoped)
    return JSON.parse(localStorage.getItem(_LEGACY_PINNED_KEY) || '[]')
  } catch {
    return []
  }
}
const _savePinned = (root, ids) => {
  try {
    localStorage.setItem(_pinnedKey(root), JSON.stringify(ids))
  } catch {
    /* noop */
  }
}

export const pinToTop = function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return
  const current = new Set(_loadPinned(root))
  if (current.has(channelId)) current.delete(channelId)
  else current.add(channelId)
  const arr = Array.from(current)
  _savePinned(root, arr)
  root.update({ _pinnedChannels: arr })
}

// Load pinned channels from localStorage into state on init
export const loadPinnedChannels = function () {
  const root = this?.state?.root
  if (!root) return
  root.update({ _pinnedChannels: _loadPinned(root) })
}

// ── Add members to an existing channel ──
// Accepts an array of email strings. Inserts membership rows via the same
// SDK path used by createChannel, then merges them into root.channelMembers.
// Idempotent: duplicate-key errors are swallowed (server-side 23505).
//
// CHAT-ADD-MEMBERS-1 — "Unable to add members in existing channel": a
// rejected write (403 non-owner mutation, network error, anything past the
// duplicate-key swallow above) had no catch anywhere in the chain — this
// function's own `await Promise.all(...)` and the button's `await
// el.call('addMembersToChannel', …)` (ChannelSettingsModal.js) both let it
// reject uncaught. The panel then stayed open with the picks still selected
// and root.channelMembers never grew, which reads as "does nothing" — same
// failure class CHAT-ACTIONS-1 already fixed for delete/react (see
// messageActions.js's deleteMessage). Returns `true` on success / `false` on
// failure so the caller can decide whether to close the panel; surfaces the
// failure the same way deleteMessage does instead of console-only.
//
// `emailsToAdd` entries are emails or `uid:<user id>` picker keys — a person
// the caller knows only by id (the reduced /people projection carries no
// peer email). An id row goes up as { user_id } and the server resolves it
// within the channel's workspace; the row it answers (member_email +
// user_id) is what lands in root.channelMembers.
export const addMembersToChannel = async function (channelId, emailsToAdd) {
  const root = this?.state?.root
  if (!root || !channelId || !Array.isArray(emailsToAdd) || !emailsToAdd.length) return false
  const sdk = getSDK()
  const _norm = (e) => (typeof e === 'string' ? e.toLowerCase().trim() : null)
  const mine = (root.channelMembers || []).filter((m) => m.channel_id === channelId)
  const existingEmails = new Set(mine.map((m) => m.member_email))
  const existingIds = new Set(
    mine
      .map((m) => m.user_id)
      .filter((v) => v != null)
      .map(String)
  )
  const keys = emailsToAdd.map(_norm).filter(Boolean)
  const newEmails = keys.filter((k) => !k.startsWith('uid:') && !existingEmails.has(k))
  const newIds = keys
    .filter((k) => k.startsWith('uid:'))
    .map((k) =>
      emailsToAdd
        .find((e) => _norm(e) === k)
        .trim()
        .slice(4)
    )
    .filter((id) => id && !existingIds.has(id))
  if (!newEmails.length && !newIds.length) return true

  // The person and the channel, never a role: the server decides who joins
  // as what (a new row is a member; an existing owner or admin keeps their
  // role). A client-chosen role let any member add people as admins, and a
  // re-add stamped `member` over an owner (the coordinator's 2026-10-28
  // contract with the core fix; a role picker for admins would be a separate,
  // explicit control).
  const rows = [
    ...newEmails.map((email) => ({ channel_id: channelId, member_email: email })),
    ...newIds.map((id) => ({ channel_id: channelId, user_id: id }))
  ]
  const _write = sameWorkspaceWriter(root)
  let answered = []
  try {
    const results = await Promise.all(
      rows.map((row) =>
        sdk
          .execute('workspaceProject.chat.members', 'create', {
            channelId,
            payload: row
          })
          .catch((err) => {
            const msg = err?.message || ''
            if (/duplicate|already exists|conflict|409|23505/i.test(msg)) return null
            throw err
          })
      )
    )
    answered = results.flatMap((res) => {
      const data = res && (res.data !== undefined ? res.data : res)
      return (Array.isArray(data) ? data : [data]).filter((r) => r && r.member_email)
    })
  } catch (err) {
    console.error('[addMembersToChannel]', err?.message || err)
    try {
      this.call('openNotification', {
        title: this.call('polyglot', 'chat.error.addMembersTitle'),
        message: err?.message || this.call('polyglot', 'chat.error.networkMessage'),
        type: 'error'
      })
    } catch {}
    return false
  }
  // The server's rows first (they carry the email an id resolved to, and
  // user_id), then the email rows we sent; upsert on (channel, email) so a
  // realtime member.insert that landed first is not doubled.
  const now = new Date().toISOString()
  const incoming = [
    ...answered.map((r) => ({ ...r, channel_id: r.channel_id || channelId })),
    ...rows.filter((r) => r.member_email)
  ]
  const next = (root.channelMembers || []).slice()
  const key = (r) => `${r.channel_id}|${_norm(r.member_email)}`
  const at = new Map(next.map((r, i) => [key(r), i]))
  for (const row of incoming) {
    const k = key(row)
    if (at.has(k)) next[at.get(k)] = { ...next[at.get(k)], ...row }
    else {
      at.set(k, next.length)
      next.push({ ...row, last_read_at: now })
    }
  }
  _write({ channelMembers: next })
  return true
}

// ── Remove a member from a channel (owner / admin at the server) ──
// Returns true when the row is gone, false when the server refused. A refusal
// says so (the same error notice addMembersToChannel uses): it used to log
// only, so the row stayed and nothing told the person why.
export const removeMemberFromChannel = async function (channelId, memberEmail) {
  const root = this?.state?.root
  if (!root || !channelId || !memberEmail) return false
  const _write = sameWorkspaceWriter(root)
  try {
    await getSDK().execute('workspaceProject.chat.members', 'remove', {
      channelId,
      userId: memberEmail
    })
  } catch (err) {
    console.error('[removeMemberFromChannel]', err?.message || err)
    try {
      this.call('openNotification', {
        title: this.call('polyglot', 'chat.error.removeMemberTitle'),
        message: err?.message || this.call('polyglot', 'chat.error.networkMessage'),
        type: 'error'
      })
    } catch {}
    return false
  }
  _write({
    channelMembers: (root.channelMembers || []).filter(
      (m) => !(m.channel_id === channelId && m.member_email === memberEmail)
    )
  })
  return true
}

// ── Invite — straight into the Add people sheet (chatModals.js) ──
export const inviteToChannel = function (channelId) {
  if (!channelId) return
  this.call('openAddPeopleModal', channelId)
}

// ── Channel settings — open settings modal ──
export const openChannelSettings = function (channelId) {
  this.call('openChannelSettingsModal', channelId)
}

// ── Start group chat from a DM — open new channel modal prefilled ──
export const startGroupChat = function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return
  const me = root.currentUserEmail
  const members = (root.channelMembers || [])
    .filter((m) => m.channel_id === channelId && m.member_email !== me)
    .map((m) => m.member_email)
  this.call('openNewChannelModal', { kind: 'dm', members })
}

// ── Convert group DM to channel ──
export const convertToChannel = function (channelId) {
  const root = this?.state?.root
  if (!root || !channelId) return
  const me = root.currentUserEmail
  const members = (root.channelMembers || [])
    .filter((m) => m.channel_id === channelId && m.member_email !== me)
    .map((m) => m.member_email)
  this.call('openNewChannelModal', { kind: 'channel', members })
}
